import {ChangeDetectionStrategy, Component, computed, input, model, output} from '@angular/core';
import {
  BerthVisitSearch,
  DownloadableFile,
  HarbourDuesInvoice,
  HarbourDuesVisitSummary,
  LastEditedBy,
  TaskMessageStatus,
} from "@portbase/bezoekschip-service-typescriptmodels";
import {AppContext} from "../../../app-context";
import {DateRange} from "../../../common/date/date-range/date-range";
import {ArrivalStatus, MovementInfo} from "../../common/movement-icons/movement-icons.component";
import {SentenceCasePipe} from "../../../common/sentence-case.pipe";
import {sendCommand, sendQuery} from "../../../common/utils";
import {HarbourDuesOverviewUtils} from "./harbour-dues-overview.utils";
import moment from "moment/moment";
import {sum} from "lodash";

@Component({
  selector: 'app-harbour-dues-overview-item',
  templateUrl: './harbour-dues-overview-item.component.html',
  styleUrls: ['./harbour-dues-overview-item.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HarbourDuesOverviewItemComponent {
  appContext = AppContext;

  harbourDuesVisitSummary = model<NewHarbourDuesVisitSummary>(undefined, {alias: 'summary'});
  term = input<string>();
  zoomVesselImage = output<NewHarbourDuesVisitSummary>();

  entryDate = computed(() => this.harbourDuesVisitSummary().ataPort || this.harbourDuesVisitSummary().etaPort);
  exitDate = computed(() => this.harbourDuesVisitSummary().atdPort || this.harbourDuesVisitSummary().etdPort);
  entryPointName = computed(() =>
    this.harbourDuesVisitSummary().entryPoint?.name || 'No entry point');
  exitPointName = computed(() =>
    this.harbourDuesVisitSummary().exitPoint?.name || 'No exit point');
  movements = computed(() => {
    const summary = this.harbourDuesVisitSummary();
    const movements: MovementInfo[] = [];
    movements.push({
      name: SentenceCasePipe.format(this.entryPointName()),
      arrivalStatus: summary.ataPort ? ArrivalStatus.arrived : ArrivalStatus.expected,
      date: this.entryDate()
    });
    summary.berthVisitSearches.forEach(bv => {
      movements.push({
        name: SentenceCasePipe.format(bv.name || bv.terminalName || bv.stevedore || 'No berth(s)'),
        arrivalStatus: bv.atd ? ArrivalStatus.departed : bv.ata ? ArrivalStatus.arrived : ArrivalStatus.expected,
        date: bv.ata || bv.atd || bv.eta || bv.etd
      })
    })
    movements.push({
      name: SentenceCasePipe.format(this.exitPointName()),
      arrivalStatus: summary.atdPort ? ArrivalStatus.arrived : ArrivalStatus.expected,
      date: this.exitDate()
    });
    return movements;
  });

  navigationUrl = computed(() => {
    return '/details/' + this.harbourDuesVisitSummary().crn + '/harbourDues';
  });

  tryRefreshHarbourDuesVisitSummary = () => {
    sendCommand('com.portbase.bezoekschip.common.api.harbourdues.summary.RefreshHarbourDuesVisitSummary', {
      crn: this.harbourDuesVisitSummary().crn
    }, (summary: HarbourDuesVisitSummary) => {
      this.harbourDuesVisitSummary.set(HarbourDuesOverviewUtils.toExtendedSummary(summary));
      AppContext.registerSuccess('Harbour dues visit summary has been rebuild successfully.');
    });
  }
  protected readonly moment = moment;
  protected readonly declarationStatus = computed(() => {
    return this.getHarbourDuesStatuses();
  });
  protected readonly sum = sum;

  protected readonly invoices = computed(() => {
    const summary = this.harbourDuesVisitSummary();
    if (!summary?.harbourDues) return [];
    return summary.harbourDues.flatMap(hd => hd.invoices || []);
  });

  downloadPdf(invoice: HarbourDuesInvoice) {
    const summary = this.harbourDuesVisitSummary();
    const harbourDue = summary.harbourDues?.find(hd => hd.invoices?.includes(invoice));
    sendQuery('com.portbase.bezoekschip.common.api.harbourdues.queries.GetHarbourDuesDeclarationPdfRotterdam', {
      url: invoice.url,
      crn: summary.crn,
      type: invoice.invoiceType,
      statementNumber: harbourDue?.statementNumber,
      invoiceNumber: invoice.invoiceNumber
    }, {caching: false, showSpinner: true}).subscribe((file: DownloadableFile) => {
      const base64 = file.data.replace(/\s/g, '');
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {type: file.mediaType || 'application/pdf'});
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    });
  }


  private getHarbourDuesStatuses(): TaskMessageStatus {
    let taskStatus = this.harbourDuesVisitSummary().harbourDuesVisitSummaryStatus !== TaskMessageStatus.DISABLED
      ? this.harbourDuesVisitSummary().harbourDuesVisitSummaryStatus : TaskMessageStatus.UNKNOWN;

    if (this.harbourDuesVisitSummary().portUnCode === 'NLRTM') {
      const deadline = this.harbourDuesVisitSummary().deadlines?.["HARBOUR_DUES"];
      if (deadline && taskStatus === TaskMessageStatus.UNKNOWN) {
        const now = moment();
        if (deadline.end && now.isAfter(moment(deadline.end))) {
          taskStatus = TaskMessageStatus.DEADLINE_MISSED;
        } else if (deadline.start && now.isAfter(moment(deadline.start))) {
          taskStatus = TaskMessageStatus.DEADLINE_CLOSE;
        }
      }
    }

    return taskStatus;
  }
}

interface OverviewFilter {
  dateRange: DateRange;
  searchTerm;
  ownCallsOnly;
}

interface OverviewCategory {
  name: string,
  property: string,
  showOnLoad: boolean,
  visible: boolean
}

interface OpenTask {
  color: string,
  icon: string,
  tooltip: string
}

export interface NewHarbourDuesVisitSummary extends HarbourDuesVisitSummaryExpanded {
  currentBerthVisit?: BerthVisitSearch;
  lastEditedByUser?: LastEditedBy;
}

export interface HarbourDuesVisitSummaryExpanded extends HarbourDuesVisitSummary {
  calculatedOpenTasks?: OpenTask[];
  vesselImageUrl?: string;
}
